/**
 * useSubscription Hook
 * Custom hook to manage access based on subscription plan
 */

import { useState, useEffect } from 'react';
import useAuthStore from '../store/authStore';
import client from '../api/client';

export const PLAN_LIMITS = {
  free: {
    name: 'Gratuit',
    canCreateScans: false,
    canUseOSINT: false,
    canUseEnrichment: false,
    canUseAI: false,
    maxScansPerMonth: 0,
    features: []
  },
  pro: {
    name: 'Pro',
    canCreateScans: true,
    canUseOSINT: true,
    canUseEnrichment: true,
    canUseAI: true,
    maxScansPerMonth: 50,
    features: ['scans', 'osint', 'enrichment', 'ai_analysis']
  },
  entreprise: {
    name: 'Entreprise',
    canCreateScans: true,
    canUseOSINT: true,
    canUseEnrichment: true,
    canUseAI: true,
    maxScansPerMonth: 200,
    features: ['scans', 'osint', 'enrichment', 'ai_analysis', 'exploitation']
  },
  groupe: {
    name: 'Groupe',
    canCreateScans: true,
    canUseOSINT: true,
    canUseEnrichment: true,
    canUseAI: true,
    maxScansPerMonth: -1, // unlimited
    features: ['scans', 'osint', 'enrichment', 'ai_analysis', 'exploitation']
  },
  dedicated: {
    name: 'Dedicated',
    canCreateScans: true,
    canUseOSINT: true,
    canUseEnrichment: true,
    canUseAI: true,
    maxScansPerMonth: 100,
    features: ['scans', 'osint', 'enrichment', 'ai_analysis', 'exploitation']
  }
};

export default function useSubscription() {
  const { user } = useAuthStore();
  const [subscription, setSubscription] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchSubscription();
  }, [user]);

  const fetchSubscription = async () => {
    if (!user || !user.company_id) {
      setLoading(false);
      return;
    }

    try {
      // Try to fetch subscription info via the API
      const response = await client.get('/api/v1/account/subscription');
      setSubscription(response.data);
    } catch (error) {
      console.error('Failed to fetch subscription:', error);
      // Default to free plan
      setSubscription({ plan: 'free' });
    } finally {
      setLoading(false);
    }
  };

  // Determine the current plan
  const currentPlan = subscription?.plan || 'free';
  const planLimits = PLAN_LIMITS[currentPlan] || PLAN_LIMITS.free;

  // Check if user is super admin (bypasses all restrictions)
  const isSuperAdmin = user?.role === 'super_admin' || user?.is_superuser;

  return {
    // Informations sur le plan
    plan: currentPlan,
    planName: planLimits.name,
    subscription,
    loading,

    // Permissions (super admin bypass tout)
    canCreateScans: isSuperAdmin || planLimits.canCreateScans,
    canUseOSINT: isSuperAdmin || planLimits.canUseOSINT,
    canUseEnrichment: isSuperAdmin || planLimits.canUseEnrichment,
    canUseAI: isSuperAdmin || planLimits.canUseAI,

    // Statut
    isFreePlan: !isSuperAdmin && currentPlan === 'free',
    isPaidPlan: currentPlan !== 'free',
    isSuperAdmin,

    // Quotas
    maxScansPerMonth: planLimits.maxScansPerMonth,
    features: planLimits.features,

    // Utilitaires
    requiresUpgrade: (feature) => {
      if (isSuperAdmin) return false;
      return !planLimits.features.includes(feature);
    },

    getUpgradeMessage: () => {
      return "Free accounts cannot perform this action. Upgrade to a Pro plan to unlock all features.";
    },

    refresh: fetchSubscription
  };
}
